/**
 * IA des ennemis normaux. Port de AEnemyDrone::UpdateFlight / TryFire, à une différence près :
 * le drone ne se déplace plus à vitesse imposée, il produit une commande pour le moteur de vol commun
 * (accélération, inertie, frein), ce qui répond à l'arbitrage ouvert n°2 de gamedesign §15.
 */
import type { EnemyProfile, EnemyRole, GameData } from '../../../data';
import { DEG, Quat, Vec3, rotateAround, UP } from '../../core/math';
import { emptyCommand, Ship, type DamageSource, type ShipCommand } from '../../entities';
import { aiMotorProfile, turnRateToward } from '../../flight/motor';
import type { SimState } from '../../state';
import { spawnEnemyBolt } from '../../weapons/projectiles';

export interface CombatTuning {
  maxSpeed: number;
  fireInterval: number;
  damage: number;
  preferredDistance: number;
  retreatDistance: number;
}

export class EnemyBrain {
  protected passTimeRemaining = 0;
  protected passDirection: Vec3;
  protected breakaway = true;
  protected charging = false;
  protected chargeRemaining = 0;
  protected chargedOrigin = new Vec3();
  protected chargedDirection = new Vec3(0, 0, -1);
  protected timeUntilNextShot: number;
  protected heavyCommit = 0;
  protected strafePhase: number;
  protected readonly tacticalSide: number;
  /** Tir ordinaire suspendu (boss pendant ses annonces et récupérations). */
  protected combatSuppressed = false;
  protected usesAttackPasses = false;
  /** Freine au lieu de se déplacer (tireur en charge, boss en annonce « hold »). */
  protected holdPosition = false;
  protected tuning: CombatTuning;
  /** Intention courante, pour l'affichage de debug. */
  intent = '';
  travelDirection = new Vec3(0, 0, -1);
  /** Âge de l'IA (délai d'armement des drones). */
  protected age = 0;
  private droneMode: 'orbit' | 'warn' | 'dive' = 'orbit';
  private droneModeTime = 0;
  private droneDiveTimer = 0;
  private droneOrbitAngle: number | null = null;
  private droneOrbitHeight = 0;

  constructor(
    protected readonly state: SimState,
    readonly ship: Ship,
    readonly role: EnemyRole | 'boss',
    protected readonly profile: EnemyProfile | null,
  ) {
    const rng = state.rng;
    this.passDirection = ship.forward();
    this.strafePhase = rng.range(0, Math.PI * 2);
    this.tacticalSide = rng.bool() ? 1 : -1;
    this.tuning = profile
      ? {
          maxSpeed: profile.maxSpeed,
          fireInterval: profile.fireInterval,
          damage: profile.projectileDamage,
          preferredDistance: profile.preferredDistance,
          retreatDistance: Math.min(profile.retreatDistance, profile.preferredDistance),
        }
      : { maxSpeed: 1, fireInterval: 1, damage: 1, preferredDistance: 1, retreatDistance: 0 };
    this.timeUntilNextShot = rng.range(0.25, this.tuning.fireInterval);
  }

  /**
   * Cible attribuée (escorte : le convoi pour les lourds, les drones et une partie des tireurs) ; le joueur par
   * défaut et dès que la cible attribuée est détruite.
   */
  targetShip: Ship | null = null;

  protected get target(): Ship {
    return this.targetShip?.alive ? this.targetShip : this.state.player;
  }

  get isCharging(): boolean { return this.charging; }

  protected get fireRange(): number { return this.profile?.fireRange ?? 100; }
  protected get projectileSpeed(): number { return this.profile?.projectileSpeed ?? 100; }
  protected get requiredFacing(): number { return this.profile?.requiredFacingDot ?? 0.9; }
  protected get weaponForward(): number { return this.profile?.weaponForward ?? 7; }
  protected get damageSource(): DamageSource { return this.role; }
  protected get boltColor(): [number, number, number] { return this.profile?.boltColor ?? [1, 0.1, 0.02]; }

  /** Produit la commande du tick et gère le tir. */
  update(dt: number): ShipCommand {
    const player = this.target;
    this.age += dt;
    if (!player.alive) {
      const idle = emptyCommand();
      idle.brake = true;
      return idle;
    }
    if (this.role === 'drone') return this.droneCommand(dt);
    const command = this.charging || this.holdPosition ? this.holdCommand(dt) : this.flightCommand(dt);
    if (!this.combatSuppressed) this.tryFire(dt);
    return command;
  }

  /** Réaction au blocage par un obstacle pendant le déplacement du tick précédent. */
  onBlocked(normal: Vec3): void {
    // Un drone qui heurte le décor à pleine vitesse explose.
    // Un drone qui heurte le décor n'explose qu'en piqué ; sinon l'évitement l'en écarte.
    if (this.role === 'drone') {
      if (this.droneMode === 'dive') this.detonate();
      return;
    }
    if (this.role !== 'interceptor') return;
    const p = this.profile as GameData['enemies']['roles']['interceptor'];
    this.passDirection = normal.scale(1.8).add(this.ship.up().scale(0.35)).normalized();
    this.passTimeRemaining = p.blockedBreakawayDuration;
    this.breakaway = true;
  }

  protected holdCommand(dt: number): ShipCommand {
    const cmd = emptyCommand();
    cmd.brake = true;
    const toPlayer = this.target.position.sub(this.ship.position).normalized(this.ship.forward());
    const facing = this.charging ? this.chargedDirection : toPlayer;
    cmd.turnRate = turnRateToward(this.ship.rotation, Quat.lookRotation(facing, UP()), this.ship.motor.maxTotalRate, dt);
    this.intent = this.charging ? 'charge' : 'maintien';
    return cmd;
  }

  protected flightCommand(dt: number): ShipCommand {
    const ship = this.ship;
    const player = this.target;
    const toTarget = player.position.sub(ship.position);
    const distance = toTarget.length();
    const targetDir = toTarget.normalized(ship.forward());
    let travel: Vec3;
    const t = this.tuning;

    if (this.role === 'interceptor') {
      const p = this.profile as GameData['enemies']['roles']['interceptor'];
      this.passTimeRemaining -= dt;
      const beginAttack = () => {
        const side = Math.sin(this.strafePhase) >= 0 ? 1 : -1;
        const vertical = Math.cos(this.strafePhase * 0.73) >= 0 ? 1 : -1;
        const predicted = player.position.add(player.velocity.scale(p.leadTime));
        const aim = predicted.add(player.right().scale(side * p.passLateral)).add(player.up().scale(vertical * p.passVertical));
        this.passDirection = aim.sub(ship.position).normalized(ship.forward());
        this.passTimeRemaining = Math.max(p.passTimeMin, Math.min(p.passTimeMax, (distance / Math.max(t.maxSpeed, 1)) * p.passTimeFactor));
        this.breakaway = false;
        this.strafePhase += 1.7;
      };
      const beginBreakaway = () => {
        const side = Math.sin(this.strafePhase) >= 0 ? 1 : -1;
        const vertical = Math.cos(this.strafePhase * 0.61) >= 0 ? 1 : -1;
        this.passDirection = targetDir.scale(-0.85).add(player.right().scale(side)).add(player.up().scale(vertical * 0.55)).normalized();
        this.passTimeRemaining = p.breakawayDuration;
        this.breakaway = true;
        this.strafePhase += 1.2;
      };
      if (!this.breakaway && distance <= t.retreatDistance) beginBreakaway();
      else if (this.passTimeRemaining <= 0) {
        if (this.breakaway || distance > t.preferredDistance) beginAttack();
        else beginBreakaway();
      }
      travel = this.passDirection;
      this.intent = this.breakaway ? 'dégagement' : 'passe';
    } else if (this.usesAttackPasses) {
      travel = this.attackPassDirection(dt, distance);
      this.intent = 'passe';
    } else if (this.role === 'heavy') {
      travel = this.heavyDirection(dt, distance, targetDir);
    } else if (distance > t.preferredDistance + this.approachMargin()) {
      travel = targetDir;
      this.intent = 'approche';
    } else if (distance < t.retreatDistance) {
      travel = targetDir.neg();
      this.intent = 'recul';
    } else {
      this.strafePhase += dt * 0.8;
      travel = ship.right().scale(Math.cos(this.strafePhase)).add(ship.up().scale(Math.sin(this.strafePhase))).normalized(ship.right());
      this.intent = 'glissement';
    }

    travel = this.keepOutOfCarrier(this.avoid(travel));
    this.travelDirection = travel;
    const cmd = emptyCommand();
    const maxRate = ship.motor.maxTotalRate;
    if (this.role === 'marksman') {
      // Le tireur garde le nez sur le joueur et translate indépendamment.
      cmd.turnRate = turnRateToward(ship.rotation, Quat.lookRotation(targetDir, UP()), maxRate, dt);
      const local = ship.rotation.conjugate().rotate(travel);
      cmd.thrust.set(local.x, local.y, -local.z);
    } else {
      // Les mobiles orientent leur nez vers la trajectoire et avancent sur leur axe.
      cmd.turnRate = turnRateToward(ship.rotation, Quat.lookRotation(travel, UP()), maxRate, dt);
      cmd.thrust.set(0, 0, 1);
    }
    return cmd;
  }

  protected approachMargin(): number {
    return this.role === 'marksman' ? (this.profile as GameData['enemies']['roles']['marksman']).approachMargin : 3.5;
  }

  /** Passes d'attaque du boss en Ouverture (bUsesAttackPasses). */
  protected attackPassDirection(dt: number, distance: number): Vec3 {
    const pass = this.state.data.boss.openingPass;
    const player = this.target;
    this.passTimeRemaining -= dt;
    if (this.passTimeRemaining <= 0) {
      const side = Math.sin(this.strafePhase) >= 0 ? 1 : -1;
      const vertical = Math.cos(this.strafePhase * 0.67) >= 0 ? 1 : -1;
      const aim = player.position.add(player.right().scale(side * pass.lateral)).add(player.up().scale(vertical * pass.vertical));
      this.passDirection = aim.sub(this.ship.position).normalized(this.ship.forward());
      this.passTimeRemaining = Math.max(pass.timeMin, Math.min(pass.timeMax, (distance / Math.max(this.tuning.maxSpeed, 1)) * pass.timeFactor));
      this.strafePhase += 2.4;
    }
    return this.passDirection;
  }

  /** Lourd : contournement vers l'angle mort arrière, fenêtre d'engagement (EnemyDrone.cpp:499-545). */
  protected heavyDirection(dt: number, distance: number, targetDir: Vec3): Vec3 {
    const p = this.profile as GameData['enemies']['roles']['heavy'];
    const t = this.tuning;
    const player = this.target;
    this.heavyCommit = Math.max(0, this.heavyCommit - dt);
    this.strafePhase += dt * 0.72;
    const pf = player.forward(), pr = player.right(), pu = player.up();
    const anchor = player.position
      .sub(pf.scale(t.preferredDistance))
      .add(pr.scale(this.tacticalSide * t.preferredDistance * p.blindSpotLateral))
      .add(pu.scale(Math.sin(this.strafePhase) * t.preferredDistance * p.blindSpotVertical));
    const toAnchor = anchor.sub(this.ship.position);
    const fromPlayer = targetDir.neg();
    let tangent = pu.cross(fromPlayer).normalized();
    if (tangent.isNearlyZero()) tangent = pr;
    tangent = tangent.scale(this.tacticalSide);
    const active = tangent.add(pu.scale(Math.cos(this.strafePhase) * 0.32)).sub(pf.scale(0.28)).normalized(tangent);
    const frontAlignment = pf.dot(fromPlayer);
    const radial = Math.max(-0.45, Math.min(0.45, (distance - t.preferredDistance) / Math.max(t.preferredDistance, 1)));
    if (distance < t.retreatDistance) {
      this.intent = 'recul';
      return targetDir.scale(-0.9).add(active.scale(1.15)).normalized(active);
    }
    if (this.heavyCommit > 0) {
      this.intent = 'engagement';
      return targetDir;
    }
    if (frontAlignment > -0.35) {
      this.intent = 'contournement';
      return active.scale(1.25).add(targetDir.scale(radial * 0.7)).normalized(active);
    }
    if (toAnchor.lengthSq() > (t.preferredDistance * 0.18) ** 2) {
      this.intent = 'angle mort';
      return toAnchor.normalized().add(active.scale(0.42)).normalized(active);
    }
    this.intent = 'pression arrière';
    return active;
  }

  /**
   * Drone en nuée : orbite autour du joueur en tirant de petits projectiles ; de temps en temps,
   * piqué annoncé (alerte puis plongée), limité en nombre simultané ; s'il manque sa cible, il remonte.
   */
  protected droneCommand(dt: number): ShipCommand {
    const p = this.profile as GameData['enemies']['roles']['drone'];
    const state = this.state;
    const ship = this.ship;
    const player = this.target;
    const cmd = emptyCommand();
    const toPlayer = player.position.sub(ship.position);
    const distance = toPlayer.length();
    if (this.droneOrbitAngle === null) {
      // Place d'orbite propre à chaque drone, à partir de sa position d'arrivée.
      const rel = ship.position.sub(player.position);
      this.droneOrbitAngle = Math.atan2(rel.dot(player.forward()), rel.dot(player.right()));
      this.droneOrbitHeight = state.rng.range(-p.orbitHeight, p.orbitHeight);
      this.droneDiveTimer = p.firstDiveDelay + state.rng.range(0, p.diveCooldownMax - p.diveCooldownMin);
    }
    this.droneDiveTimer -= dt;

    if (this.droneMode === 'dive') {
      this.droneModeTime += dt;
      if (distance <= p.detonationRadius) {
        this.detonate();
        return cmd;
      }
      // Piqué manqué : joueur dépassé, ou plongée trop longue.
      const passed = ship.velocity.dot(toPlayer) < 0 && distance > p.detonationRadius * 2.5;
      if (passed || this.droneModeTime > p.diveMaxTime) {
        this.endDive();
      } else {
        const aim = player.position.add(player.velocity.scale(p.leadTime * Math.min(1, distance / 80)));
        const travel = this.keepOutOfCarrier(this.avoid(aim.sub(ship.position).normalized(ship.forward())));
        this.travelDirection = travel;
        cmd.thrust.set(0, 0, 1);
        cmd.turnRate = turnRateToward(ship.rotation, Quat.lookRotation(travel, UP()), ship.motor.maxTotalRate, dt);
        this.intent = 'piqué';
        return cmd;
      }
    }

    if (this.droneMode === 'warn') {
      this.droneModeTime += dt;
      if (this.droneModeTime >= p.diveTelegraph) {
        this.droneMode = 'dive';
        this.droneModeTime = 0;
        state.emit({ type: 'droneDive', time: state.time, shipId: ship.id, phase: 'dive' });
      }
    } else if (this.droneDiveTimer <= 0 && this.age > p.armTime && player.alive) {
      const diving = state.ships.filter((s) => s.alive && s.kind === 'drone' && s.diving).length;
      const inFront = player.forward().dot(toPlayer.neg().normalized()) >= p.diveFrontDot;
      if (diving < p.maxConcurrentDives && distance < p.orbitRadius * 1.5 && inFront) {
        this.droneMode = 'warn';
        this.droneModeTime = 0;
        ship.diving = true;
        state.emit({ type: 'droneDive', time: state.time, shipId: ship.id, phase: 'warn' });
      } else {
        // Réessaie bientôt : le drone attend de passer devant le joueur.
        this.droneDiveTimer = state.rng.range(0.3, 1);
      }
    }

    // Orbite : point qui tourne autour du joueur, vitesse réduite (poussée partielle).
    this.droneOrbitAngle += (p.orbitSpeed / p.orbitRadius) * this.tacticalSide * dt;
    const a = this.droneOrbitAngle;
    const slot = player.position
      .add(player.right().scale(Math.cos(a) * p.orbitRadius))
      .add(player.forward().scale(Math.sin(a) * p.orbitRadius))
      .add(player.up().scale(this.droneOrbitHeight * p.orbitRadius));
    const travel = this.keepOutOfCarrier(this.avoid(slot.sub(ship.position).normalized(ship.forward())));
    this.travelDirection = travel;
    const far = ship.position.distanceTo(slot);
    cmd.thrust.set(0, 0, Math.min(1, (p.orbitSpeed / ship.motor.cruiseMaxSpeed) * (far > 40 ? 1.3 : 1)));
    cmd.turnRate = turnRateToward(ship.rotation, Quat.lookRotation(travel, UP()), ship.motor.maxTotalRate, dt);
    this.intent = this.droneMode === 'warn' ? 'annonce de piqué' : 'orbite';
    // Tirs seulement depuis l'avant du joueur : le danger reste visible.
    if (this.droneMode === 'orbit' && player.forward().dot(toPlayer.neg().normalized()) >= p.fireFrontDot) this.tryFire(dt);
    return cmd;
  }

  private endDive(): void {
    const p = this.profile as GameData['enemies']['roles']['drone'];
    this.droneMode = 'orbit';
    this.droneModeTime = 0;
    this.ship.diving = false;
    this.droneDiveTimer = this.state.rng.range(p.diveCooldownMin, p.diveCooldownMax);
    this.state.emit({ type: 'droneDive', time: this.state.time, shipId: this.ship.id, phase: 'abort' });
  }

  /** État de piqué, pour le HUD. */
  get droneDiveState(): 'orbit' | 'warn' | 'dive' { return this.droneMode; }

  /** Explosion du drone : dégâts de zone (joueur et autres ennemis), puis destruction sans points. */
  protected detonate(): void {
    const ship = this.ship;
    if (!ship.alive) return;
    const p = this.profile as GameData['enemies']['roles']['drone'];
    const state = this.state;
    state.emit({ type: 'droneDetonated', time: state.time, shipId: ship.id, position: ship.position.toArray() });
    state.emit({ type: 'explosion', time: state.time, position: ship.position.toArray(), radius: p.blastRadius, color: [1, 0.45, 0.05], duration: 0.5, weapon: 'detonation' });
    ship.diving = false;
    state.applyRadialDamage(ship.position.clone(), p.blastRadius, p.detonationDamage, ship.id, { sourceShip: ship, source: 'drone', weapon: 'detonation' });
    state.applyDamage(ship, ship.health, { sourceShip: ship, source: 'drone', weapon: 'detonation', position: ship.position, origin: ship.position });
  }

  /** Les ennemis ne pénètrent pas l'enveloppe des docks (porteur, baie de la station) : ils s'en écartent progressivement. */
  protected keepOutOfCarrier(travel: Vec3): Vec3 {
    const margin = this.state.data.enemies.common.carrierKeepOutMargin;
    for (const dock of this.state.docks) {
      const depth = dock.keepOutDepth(this.ship.position) + margin;
      if (depth <= 0) continue;
      const away = this.ship.position.sub(dock.position).normalized(UP());
      return travel.add(away.scale((depth / Math.max(margin, 1)) * 2)).normalized(away);
    }
    return travel;
  }

  /** Évitement local par balayage sphérique (EnemyDrone.cpp:562-577). */
  protected avoid(travel: Vec3): Vec3 {
    const common = this.state.data.enemies.common;
    const radius = this.ship.radius;
    const distance = Math.max(common.avoidanceMinDistance, radius * common.avoidanceRadiusFactor);
    const hit = this.state.collision.sphereCast(this.ship.position, travel, radius, distance);
    if (!hit) return travel;
    return travel.add(hit.normal.scale(common.avoidanceNormalWeight)).normalized(travel);
  }

  protected weaponOrigin(): Vec3 {
    return this.ship.position.add(this.ship.forward().scale(this.weaponForward));
  }

  protected tryFire(dt: number): void {
    const state = this.state;
    const ship = this.ship;
    const common = state.data.enemies.common;
    if (this.charging) {
      this.chargeRemaining -= dt;
      if (this.chargeRemaining <= 0) {
        spawnEnemyBolt(state, {
          kind: 'railgun',
          owner: ship,
          source: this.damageSource,
          origin: this.chargedOrigin,
          direction: this.chargedDirection,
          speed: this.projectileSpeed,
          range: this.fireRange,
          damage: this.tuning.damage,
          color: this.boltColor,
        });
        this.charging = false;
        const extra = this.role === 'marksman' ? (this.profile as GameData['enemies']['roles']['marksman']).chargeExtraCooldown : 0;
        this.timeUntilNextShot = this.tuning.fireInterval + extra;
      }
      return;
    }
    this.timeUntilNextShot -= dt;
    if (this.timeUntilNextShot > 0) return;

    const player = this.target;
    const origin = this.weaponOrigin();
    const toShip = player.position.sub(origin);
    const retry = Math.min(this.tuning.fireInterval, common.blockedRetryDelay);
    if (toShip.lengthSq() > this.fireRange ** 2) {
      this.timeUntilNextShot = retry;
      return;
    }
    if (ship.forward().dot(toShip.normalized()) < this.requiredFacing) {
      if (this.role === 'heavy') this.heavyCommit = Math.max(this.heavyCommit, (this.profile as GameData['enemies']['roles']['heavy']).engagementWindow);
      this.timeUntilNextShot = retry;
      return;
    }
    if (!state.collision.lineOfSight(origin, player.position)) {
      this.timeUntilNextShot = retry;
      return;
    }

    if (this.role === 'marksman') {
      const profile = this.profile as GameData['enemies']['roles']['marksman'];
      this.charging = true;
      this.chargeRemaining = Math.max(0.05, profile.chargeDuration > 0 ? profile.chargeDuration : 0.95);
      this.chargedOrigin = origin;
      this.chargedDirection = toShip.normalized();
      state.emit({
        type: 'telegraph',
        time: state.time,
        shipId: ship.id,
        kind: 'railgun',
        lanes: [{ origin: origin.toArray(), end: origin.add(this.chargedDirection.scale(this.fireRange)).toArray() }],
        duration: this.chargeRemaining,
        color: [1, 0.65, 0.05],
        anchor: ship.position.toArray(),
        follow: false,
      });
      return;
    }

    const flightTime = toShip.length() / Math.max(this.projectileSpeed, 1);
    const aim = player.position.add(player.velocity.scale(Math.max(0, Math.min(common.maxLeadTime, flightTime * common.leadFactor))));
    const direction = aim.sub(origin).normalized(ship.forward());
    if (this.role === 'heavy') {
      const profile = this.profile as GameData['enemies']['roles']['heavy'];
      const half = Math.floor(profile.projectileCount / 2);
      for (let i = -half; i <= half; i++) {
        spawnEnemyBolt(state, {
          kind: 'bolt',
          owner: ship,
          source: 'heavy',
          origin,
          direction: rotateAround(direction, ship.up(), i * profile.spreadDeg * DEG),
          speed: this.projectileSpeed,
          range: this.fireRange,
          damage: this.tuning.damage,
          color: this.boltColor,
        });
      }
      this.heavyCommit = 0;
    } else {
      spawnEnemyBolt(state, {
        kind: this.role === 'boss' ? 'bossBolt' : 'bolt',
        owner: ship,
        source: this.damageSource,
        origin,
        direction,
        speed: this.projectileSpeed,
        range: this.fireRange,
        damage: this.tuning.damage,
        color: this.boltColor,
      });
    }
    this.timeUntilNextShot = this.tuning.fireInterval;
  }
}

/** Crée un ennemi normal et son IA. */
export function createEnemy(state: SimState, role: EnemyRole, position: Vec3, rotation: Quat): { ship: Ship; brain: EnemyBrain } {
  const data = state.data.enemies;
  const profile = data.roles[role];
  const motor = aiMotorProfile({
    maxSpeed: profile.maxSpeed,
    acceleration: profile.acceleration,
    turnRateDeg: profile.turnRateDeg,
    releaseDeceleration: data.common.releaseDeceleration,
    brakeDeceleration: data.common.brakeDeceleration,
    turningBoost: data.common.turningBoost,
  });
  const ship = new Ship(state.newId(), role, 'enemy', position, rotation, profile.health, profile.collisionRadius, motor);
  // Les ennemis entrent dans l'arène déjà lancés, comme dans Unreal (vitesse prescrite immédiate).
  ship.velocity.copy(rotation.forward().scale(profile.maxSpeed * 0.6));
  return { ship, brain: new EnemyBrain(state, ship, role, profile) };
}
